import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apis from '../../api';
import * as userApi from '../../api/user';
import { setToken, setUserInfo, clearAuth, getToken, getUserInfo } from '../../utils/auth';
import { message } from 'antd';
import customerServiceWebSocket from '../../utils/websocket';
import { getUrl } from '../../utils/urlUtil';

// 获取当前用户信息
export const fetchCurrentUser = createAsyncThunk(
  'user/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apis.auth.getCurrentUser();
     
      // 检查响应数据结构
      if (!response || !response.data) {
        return rejectWithValue('获取用户信息响应数据格式错误');
      }
      
      // 保存用户信息到本地存储
      setUserInfo(response.data);
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || '获取用户信息失败');
    }
  }
);

// 获取用户权限
export const fetchUserPermissions = createAsyncThunk(
  'user/fetchUserPermissions',
  async (forceRefresh = false, { rejectWithValue, getState }) => {
    try {
      
      // 如果不是强制刷新且已有权限数据，直接返回缓存的权限
      const { permissions } = getState().user;
      if (!forceRefresh && permissions && permissions.length > 0) {
        return permissions;
      }
      
      const response = await apis.auth.getUserPermissions(forceRefresh);
    
      // 检查响应数据结构
      if (!response || !response.data) {
        return []; // 返回空数组作为回退
      }
      
      let permissionList = [];
      
      // 确保返回的是数组
      if (!Array.isArray(response.data)) {
        // 尝试从其他可能的位置获取权限数据
        if (response.data && Array.isArray(response.data.permissions)) {
          permissionList = response.data.permissions;
        } else if (response.data && Array.isArray(response.data.permissionList)) {
          permissionList = response.data.permissionList;
        }
      } else {
        permissionList = response.data;
      }
      
      // 处理权限数据格式
      let processedPermissions = [];
      
      if (permissionList.length > 0) {
        if (typeof permissionList[0] === 'string') {
          processedPermissions = permissionList;
        } else if (typeof permissionList[0] === 'object') {
          // 检查常见的权限码字段
          if (permissionList[0].permissionCode) {
            processedPermissions = permissionList.map(item => item.permissionCode);
          } else if (permissionList[0].code) {
            processedPermissions = permissionList.map(item => item.code);
          } else if (permissionList[0].key) {
            processedPermissions = permissionList.map(item => item.key);
          }
        }
      }
      
      // 将权限数据保存到localStorage，以便刷新页面时可以恢复
      localStorage.setItem('userPermissions', JSON.stringify(processedPermissions));
      
      return processedPermissions;
    } catch (error) {
      console.error('获取权限失败:', error);
      return rejectWithValue(error.message || '获取用户权限失败');
    }
  }
);

// 用户登录
export const login = createAsyncThunk(
  'user/login',
  async ({ username, password }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apis.auth.login(username, password);
      console.log('登录响应:', response);
      
      // 检查响应数据结构
      if (!response || !response.data) {
        return rejectWithValue('登录响应数据格式错误');
      }
      
      const { token, adminInfo } = response.data;
      
      // 保存token和用户信息
      if (token) {
        setToken(token);
        if (adminInfo) {
          setUserInfo(adminInfo);
        }
      } else {
        return rejectWithValue('登录失败: 未获取到token');
      }
      
      return {
        token,
        user: adminInfo
      };
    } catch (error) {
      return rejectWithValue(error.message || '登录失败');
    }
  }
);

// 用户登出
export const logout = createAsyncThunk(
  'user/logout',
  async (_, { rejectWithValue }) => {
    try {
      await apis.auth.logout();
      // 清除本地存储的token、用户信息和权限数据
      clearAuth();
      localStorage.removeItem('userPermissions');
      
      // 关闭 WebSocket 连接
      try {
        customerServiceWebSocket.close();
        console.log('WebSocket连接已关闭');
      } catch (wsError) {
        console.error('关闭WebSocket连接时出错:', wsError);
      }
      
      return true;
    } catch (error) {
      // 即使API调用失败，也清除本地存储
      clearAuth();
      localStorage.removeItem('userPermissions');
      
      // 尝试关闭 WebSocket 连接
      try {
        customerServiceWebSocket.close();
      } catch (wsError) {
        console.error('关闭WebSocket连接时出错:', wsError);
      }
      
      return rejectWithValue(error.message);
    }
  }
);

// 获取普通用户列表
export const fetchUserList = createAsyncThunk(
  'user/fetchUserList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await userApi.getUserList(params);
      if (!response || !response.data) {
        return rejectWithValue('获取用户列表响应数据格式错误');
      }
      // 返回数据和查询参数，以便在reducer中保存
      return {
        data: response.data,
        params
      };
    } catch (error) {
      return rejectWithValue(error.message || '获取用户列表失败');
    }
  }
);

// 获取管理员列表
export const fetchAdminList = createAsyncThunk(
  'user/fetchAdminList',
  async (params, { rejectWithValue }) => {
    try { 
      const response = await userApi.getAdminList(params);
      if (!response || !response.data) {
        return rejectWithValue('获取管理员列表响应数据格式错误');
      }
      // 返回数据和查询参数，以便在reducer中保存
      return {
        data: response.data,
        params
      };
    } catch (error) {
      return rejectWithValue(error.message || '获取管理员列表失败');
    }
  }
);

// 创建管理员
export const createUser = createAsyncThunk(
  'user/createUser',
  async (userData, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await userApi.createAdmin(userData);
      if (!response || response.code !== 200) {
        return rejectWithValue(response?.message || '创建管理员失败');
      }else{
        message.success('创建管理员成功');
      }
      
      // 创建成功后重新获取管理员列表
      const { user } = getState();
      const { adminList } = user;
      const params = {
        pageNum: adminList.pageNum || 1,
        pageSize: adminList.pageSize || 10,
        // 保留搜索参数
        ...adminList.searchParams
      };
      dispatch(fetchAdminList(params));
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || '创建管理员失败');
    }
  }
);

// 更新管理员
export const updateUser = createAsyncThunk(
  'user/updateUser',
  async ({ id, data }, { rejectWithValue, dispatch, getState }) => {
    try {
      // 判断是更新管理员还是普通用户
      const isAdmin = getState().user.activeTab === 'admin';
      let response;
      
      if (isAdmin) {
        // 更新管理员
        response = await userApi.updateAdmin(id, data);
      } else {
        // 处理普通用户的更新逻辑
        if (data.userStatus === 'BANNED') {
          // 禁用普通用户
          response = await userApi.disableNormalUser(id);
          if(response.code == 200){
            message.success('禁用成功');
          }else{
            message.error('禁用失败');
          }
        } else if (data.userStatus === 'NORMAL') {
          // 启用普通用户
          response = await userApi.enableNormalUser(id);
          if(response.code == 200){
            message.success('启用成功');
          }else{
            message.error('启用失败');
          }
        } else {
          return rejectWithValue('不支持的操作');
        }
      }
      
      if (!response || response.code !== 200) {
        return rejectWithValue(response?.message || '更新用户失败');
      }
      
      // 更新成功后重新获取列表
      const { user } = getState();
      if (isAdmin) {
        const { adminList } = user;
        const params = {
          pageNum: adminList.pageNum || 1,
          pageSize: adminList.pageSize || 10,
          ...adminList.searchParams
        };
        dispatch(fetchAdminList(params));
      } else {
        const { userList } = user;
        const params = {
          pageNum: userList.pageNum || 1,
          pageSize: userList.pageSize || 10,
          ...userList.searchParams
        };
        dispatch(fetchUserList(params));
      }
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || '更新用户失败');
    }
  }
);

// 分配管理员角色
export const assignAdminRoles = createAsyncThunk(
  'user/assignAdminRoles',
  async ({ userId, roleIds }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await userApi.assignAdminRoles(userId, roleIds);
      if (!response || response.code !== 200) {
        return rejectWithValue(response?.message || '分配角色失败');
      }
      
      // 分配成功后重新获取管理员列表
      const { user } = getState();
      const { adminList } = user;
      const params = {
        pageNum: adminList.pageNum || 1,
        pageSize: adminList.pageSize || 10,
        ...adminList.searchParams
      };
      dispatch(fetchAdminList(params));
      
      // 如果当前用户就是被分配角色的用户，刷新权限
      const { currentUser } = getState().user;
      if (currentUser && currentUser.id === userId) {
        dispatch(fetchUserPermissions(true));
      }
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || '分配角色失败');
    }
  }
);

// 启用管理员
export const enableAdmin = createAsyncThunk(
  'user/enableAdmin',
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await userApi.enableAdmin(id);
      if (!response || response.code !== 200) {
        return rejectWithValue(response?.message || '启用管理员失败');
      }else{
        message.success('启用成功');
      }
      
      // 操作成功后重新获取管理员列表
      const { user } = getState();
      const { adminList } = user;
      const params = {
        pageNum: adminList.pageNum || 1,
        pageSize: adminList.pageSize || 10,
        ...adminList.searchParams
      };
      dispatch(fetchAdminList(params));
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || '启用管理员失败');
    }
  }
);

// 禁用管理员
export const disableAdmin = createAsyncThunk(
  'user/disableAdmin',
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await userApi.disableAdmin(id);
      if (!response || response.code !== 200) {
        return rejectWithValue(response?.message || '禁用管理员失败');
      }else{
        message.success('禁用成功');
      }
    

      // 操作成功后重新获取管理员列表
      const { user } = getState();
      const { adminList } = user;
      const params = {
        pageNum: adminList.pageNum || 1,
        pageSize: adminList.pageSize || 10,
        ...adminList.searchParams
      };
      dispatch(fetchAdminList(params));
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || '禁用管理员失败');
    }
  }
);

// 重置管理员密码（指定新密码）
export const resetAdminPassword = createAsyncThunk(
  'user/resetAdminPassword',
  async ({ id, newPassword }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await userApi.resetAdminPasswordWithNewPassword(id, newPassword);
      if (!response || response.code !== 200) {
        return rejectWithValue(response?.message || '重置管理员密码失败');
      } else {
        message.success('重置密码成功');
      }
      
      // 操作成功后重新获取管理员列表
      const { user } = getState();
      const { adminList } = user;
      const params = {
        pageNum: adminList.pageNum || 1,
        pageSize: adminList.pageSize || 10,
        ...adminList.searchParams
      };
      dispatch(fetchAdminList(params));
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || '重置管理员密码失败');
    }
  }
);

// 重置普通用户密码
export const resetNormalUserPassword = createAsyncThunk(
  'user/resetNormalUserPassword',
  async (id, { rejectWithValue }) => {
    try {
      const response = await userApi.resetNormalUserPassword(id);
      if (!response || response.code !== 200) {
        return rejectWithValue(response?.message || '重置普通用户密码失败');
      }else{
        message.success('重置密码成功');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || '重置普通用户密码失败');
    }
  }
);

// 应用初始化时重新连接 WebSocket
export const reconnectWebSocket = createAsyncThunk(
  'user/reconnectWebSocket',
  async (_, { rejectWithValue }) => {
    try {
      // 获取本地存储的用户信息
      const userInfo = getUserInfo();
      
      // 检查是否有有效的用户信息和 token
      if (!userInfo || !userInfo.userId || !getToken()) {
        return rejectWithValue('无法重连 WebSocket：未找到有效的用户信息');
      }

      // 检查 WebSocket 是否已经连接
      if (customerServiceWebSocket.isConnected()) {
        console.log('WebSocket 已连接，无需重连');
        return true;
      }

      // 尝试重新连接 WebSocket
      customerServiceWebSocket.connect(
        'service', 
        userInfo.userId, 
        null, 
        getUrl(),
        true  // 标记为手动重连
      );

      return true;
    } catch (error) {
      console.error('WebSocket 重连失败:', error);
      return rejectWithValue(error.message || 'WebSocket 重连失败');
    }
  }
);

// 初始状态
const initialState = {
  currentUser: getUserInfo(), // 从localStorage获取初始用户信息
  permissions: JSON.parse(localStorage.getItem('userPermissions') || '[]'), // 从localStorage获取初始权限
  roles: [],
  menus: [],
  token: getToken(),
  loading: false,
  error: null,
  isLoggedIn: !!getToken(),
  activeTab: 'normal', // 当前激活的标签页，'normal' 或 'admin'
  // 用户列表相关状态
  userList: {
    data: [],
    total: 0,
    pageNum: 1,
    pageSize: 10,
    loading: false,
    error: null,
    searchParams: {}
  },
  // 管理员列表相关状态
  adminList: {
    data: [],
    total: 0,
    pageNum: 1,
    pageSize: 10,
    loading: false,
    error: null,
    searchParams: {}
  },
  // 操作状态
  operationStatus: {
    loading: false,
    success: false,
    error: null
  }
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearOperationStatus: (state) => {
      state.operationStatus = {
        loading: false,
        success: false,
        error: null
      };
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理登录
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.isLoggedIn = true;
        state.currentUser = action.payload.user;
        // 确保permissions在登录时重置，避免使用上一次的权限数据
        state.permissions = [];
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || '登录失败';
        state.isLoggedIn = false;
      })
      
      // 处理登出
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.currentUser = null;
        state.permissions = [];
        state.roles = [];
        state.isLoggedIn = false;
      })
      .addCase(logout.rejected, (state) => {
        state.loading = false;
        // 即使API调用失败，也强制清除本地状态
        state.token = null;
        state.currentUser = null;
        state.permissions = [];
        state.roles = [];
        state.isLoggedIn = false;
      })
      
      // 处理获取当前用户信息
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
        state.isLoggedIn = true;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || '获取用户信息失败';
        state.isLoggedIn = false;
      })
      
      // 处理获取用户权限
      .addCase(fetchUserPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.loading = false;
       
        // 检查payload是否为有效数据
        if (!action.payload) {
          state.permissions = [];
          return;
        }
        
        try {
          // 如果是数组，处理数组
          if (Array.isArray(action.payload)) {
            // 空数组情况
            if (action.payload.length === 0) {
              state.permissions = [];
              return;
            }
      
            // 如果数组项是字符串，直接使用
            if (typeof action.payload[0] === 'string') {
              state.permissions = action.payload;
              return;
            }
            
            // 如果数组项是对象，尝试提取permissionCode
            if (typeof action.payload[0] === 'object') {
              // 检查常见的权限码字段
              if (action.payload[0].permissionCode) {
                state.permissions = action.payload.map(item => item.permissionCode);
              } else if (action.payload[0].code) {
                state.permissions = action.payload.map(item => item.code);
              } else if (action.payload[0].key) {
                state.permissions = action.payload.map(item => item.key);
              } else {
                // 如果找不到合适的字段，记录错误并使用空数组
                state.permissions = [];
              }
            }
          } else {
            // 非数组情况
            state.permissions = [];
          }
        } catch (error) {
          state.permissions = [];
        }
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || '获取权限失败';
        state.permissions = [];
      })
      
      // 获取普通用户列表
      .addCase(fetchUserList.pending, (state) => {
        state.userList.loading = true;
        state.userList.error = null;
      })
      .addCase(fetchUserList.fulfilled, (state, action) => {
        state.userList.loading = false;
        state.userList.data = action.payload.data.records || [];
        state.userList.total = action.payload.data.total || 0;
        state.userList.pageNum = action.payload.params.pageNum || 1;
        state.userList.pageSize = action.payload.params.pageSize || 10;
        state.userList.searchParams = action.payload.params;
      })
      .addCase(fetchUserList.rejected, (state, action) => {
        state.userList.loading = false;
        state.userList.error = action.payload || '获取用户列表失败';
      })

      // 获取管理员列表
      .addCase(fetchAdminList.pending, (state) => {
        state.adminList.loading = true;
        state.adminList.error = null;
      })
      .addCase(fetchAdminList.fulfilled, (state, action) => {
        state.adminList.loading = false;
        state.adminList.data = action.payload.data.records || [];
        state.adminList.total = action.payload.data.total || 0;
        state.adminList.pageNum = action.payload.params.pageNum || 1;
        state.adminList.pageSize = action.payload.params.pageSize || 10;
        state.adminList.searchParams = action.payload.params;
      })
      .addCase(fetchAdminList.rejected, (state, action) => {
        state.adminList.loading = false;
        state.adminList.error = action.payload || '获取管理员列表失败';
      })

      // 创建管理员
      .addCase(createUser.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(createUser.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload || '创建管理员失败';
      })

      // 更新用户
      .addCase(updateUser.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(updateUser.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload || '更新用户失败';
      })

      // 分配管理员角色
      .addCase(assignAdminRoles.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(assignAdminRoles.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(assignAdminRoles.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload || '分配角色失败';
      })

      // 启用管理员
      .addCase(enableAdmin.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(enableAdmin.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(enableAdmin.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload || '启用管理员失败';
      })

      // 禁用管理员
      .addCase(disableAdmin.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(disableAdmin.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(disableAdmin.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload || '禁用管理员失败';
      })

      // 重置管理员密码
      .addCase(resetAdminPassword.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(resetAdminPassword.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(resetAdminPassword.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload || '重置管理员密码失败';
      })

      // 重置普通用户密码
      .addCase(resetNormalUserPassword.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(resetNormalUserPassword.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(resetNormalUserPassword.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.error = action.payload || '重置普通用户密码失败';
      });
  },
});

export const { clearError, clearOperationStatus, setActiveTab } = userSlice.actions;

export default userSlice.reducer; 